import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

// Multimalt hero — sunny yellow canvas, teal accents, playful hand-lettered
// wordmark. The brand's soul is health + happiness, so this hero reads
// bright, natural, warm rather than dark and editorial.
// -----------------------------------------------------------------------------
const SUN = "#F5D34A";
const TEAL = "#0E7B8A";
const INK = "#1E1A00";
const ROSE = "#E37B4B";

export default function MultimaltHero({ brand, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: SUN, color: INK }}
    >
      {/* Ambient wheat-glyph — logo scaled up, low opacity */}
      <img
        aria-hidden
        src={brand.logo}
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] max-w-none opacity-[0.05]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-20 pb-24 lg:pb-32">
        <button
          onClick={() => navigate("/#brands")}
          data-testid={BRAND_IDS.backHome}
          className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-100 transition-opacity mb-10 opacity-70"
          style={{ color: INK }}
        >
          <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-6"
              style={{ color: TEAL, letterSpacing: "0.36em" }}
            >
              {t(`brandContent.${brand.slug}.category`)}
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-editorial italic leading-[1.02] text-[clamp(2.75rem,6vw,5.75rem)]"
              style={{ color: INK }}
            >
              &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
            </motion.h1>

            <p
              className="font-editorial italic mt-4 text-xl md:text-2xl"
              style={{ color: TEAL }}
            >
              {t("brandContent.multimalt.heroSub") ||
                "Barley malt + real fruit + botanicals — in a can."}
            </p>

            <p
              className="mt-10 max-w-lg text-base md:text-lg leading-[1.75]"
              style={{ color: `${INK}dd` }}
            >
              {brand.story &&
                (t(`brandContent.${brand.slug}.story`) || brand.story)}
            </p>

            <p
              className="mt-8 text-xs font-bold uppercase"
              style={{ color: INK, letterSpacing: "0.3em" }}
            >
              {t("brandContent.multimalt.attr1") || "No added sugar"}
              <span className="mx-2" style={{ color: TEAL }}>●</span>
              {t("brandContent.multimalt.attr2") || "No preservatives"}
              <span className="mx-2" style={{ color: TEAL }}>●</span>
              {t("brandContent.multimalt.attr3") || "8 B-vitamins"}
            </p>

            <Link
              to={inquiryHref}
              data-testid={BRAND_IDS.ctaInquiry}
              className="mt-12 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm"
              style={{ background: TEAL, color: SUN }}
            >
              {t("brandPage.partnerCta")}
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: SUN, color: TEAL }}
              >
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          {/* Right — Multimalt Mango can */}
          <div className="lg:col-span-6 relative flex flex-col items-center">
            <motion.img
              initial={{ opacity: 0, y: 30, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src={brand.frontImage || brand.image}
              alt="Multimalt can"
              data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
              className="max-h-[500px] w-auto mx-auto drop-shadow-[0_30px_60px_rgba(14,123,138,0.25)]"
            />

            <div
              className="mt-6 h-px w-24"
              style={{ background: `linear-gradient(90deg, transparent, ${TEAL}, transparent)` }}
            />

            <p
              className="mt-4 text-xs font-bold uppercase"
              style={{ color: INK, letterSpacing: "0.3em" }}
            >
              {t("brandContent.multimalt.skuCount") || "2 Elixirs"}
              <span className="mx-2" style={{ color: ROSE }}>●</span>
              {t("brandContent.multimalt.skuFormat") || "250 ml Can"}
              <span className="mx-2" style={{ color: ROSE }}>●</span>
              {t("brandContent.multimalt.skuUse") || "Fruit + Malt"}
            </p>
          </div>
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${TEAL} 20%, ${TEAL} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
